import React, { useEffect, useRef, useState } from "react";
import { createUserSocket } from "../utils/userSocket";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import OrderConfirmation from "../components/Order/OrderConfirmation";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";

import {
} from "@mui/material";

import {
} from "@material-ui/core";

import { resetCart } from "../redux/cartRedux";
import { resetOrderCreation } from "../redux/createOrderAction";
import {
  fetchShippingCost,
  resetShippingCost,
} from "../redux/shippingCostRedux";
import { clearSSEData, setSSEData } from "../redux/cardSseRedux";
import { userRequest } from "../api/requestMethods";
import useBlockBackNavigation from "../hooks/useBlockBackNavigation";
import { cardReturnPath, clearCardAuthContext } from "../utils/cardPayment";
import { isSafeReturnPath } from "../utils/signInReturn";

const CardPaymentDoneScreen = () => {
  const user = useSelector((state) => state.user.currentUser);

  const userId = user?.result?._id || user?._id;

  const { sseData, status } = useSelector(
    (state) => state.cardSse
  );
    const orderNumber = status === "loading" || !sseData?.orderDetails ? '' : sseData.orderDetails._id;


  const dispatch = useDispatch();
  const history = useHistory();

  // Back must not return into the checkout flow (double-order protection).
  useBlockBackNavigation("/");

  // Active Step
  const [trackOrder, setTrackOrder] = useState();


  // Latest slice state, readable inside socket handlers without re-subscribing.
  const sseDataRef = useRef(sseData);
  useEffect(() => {
    sseDataRef.current = sseData;
  }, [sseData]);

  // ── Initial order snapshot ────────────────────────────────────────────────
  // The legacy `/fw/events` + `/mobile/:id/stream` SSE channels delivered the
  // customer's latest order document; with sockets (push-only) we fetch it
  // once via REST — the customer lands here straight after paying, so their
  // newest order IS this one — then live updates arrive over the socket.
  useEffect(() => {
    if (!userId || sseDataRef.current?.orderDetails?._id) return;
    userRequest(`/mobile/${userId}/myorders`)
      .then((r) => {
        const latest = Array.isArray(r.data) ? r.data[0] : null;
        if (latest) {
          dispatch(
            setSSEData({
              orderDetails: latest,
              data: sseDataRef.current?.data ?? null,
            })
          );
          setTrackOrder(latest);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dispatch]);

  // ── Live payment + order status over Socket.IO ───────────────────────────
  // Replaces BOTH legacy SSE channels:
  //  • `/fw/events` (per-process globals — broken across dynos) → join
  //    `payment:${client_id}`; the Flutterwave webhook/verify path emits
  //    `paymentStatusUpdate` {orderId, tx_ref, flw_ref, status,
  //    processor_response, amount} (flutterWaveController.emitPaymentStatus)
  //    — `sseData.data.status` keeps working ('successful' | 'failed').
  //  • `/mobile/:id/stream` (stale-snapshot order SSE) → join
  //    `orderRoom:${client_id}`; the Mongo change stream emits `orderUpdate`
  //    (full order doc) on every order write — filtered to THIS order.
  useEffect(() => {
    if (!userId) return;

    // Carries the customer's token: their rooms need it (utils/userSocket.js).
    const socket = createUserSocket();

    socket.on("connect", () => {
      console.log("[CardDone] Socket connected, joining payment + order rooms…");
      socket.emit("join", `payment:${userId}`);
      socket.emit("join", `orderRoom:${userId}`);
    });

    socket.on("paymentStatusUpdate", (payload) => {
      console.log("[CardDone] paymentStatusUpdate:", payload?.status, payload?.tx_ref);
      dispatch(
        setSSEData({
          orderDetails: sseDataRef.current?.orderDetails ?? null,
          data: payload,
        })
      );
    });

    socket.on("orderUpdate", (updatedOrder) => {
      const trackedId = sseDataRef.current?.orderDetails?._id;
      if (trackedId && String(updatedOrder?._id) !== String(trackedId)) return;
      console.log(
        "[CardDone] orderUpdate:",
        updatedOrder?._id,
        updatedOrder?.delivery_status
      );
      dispatch(
        setSSEData({
          orderDetails: updatedOrder,
          data: sseDataRef.current?.data ?? null,
        })
      );
      setTrackOrder(updatedOrder);
    });

    return () => {
      socket.off("paymentStatusUpdate");
      socket.off("orderUpdate");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);


  // Get shipping Fees from the server
  useEffect(() => {
    dispatch(fetchShippingCost());
  }, [dispatch]);


  // Show the total the server actually charged for THIS order (authoritative
  // distance-based fee/total) — the legacy flat `/shippingCost` slice is only
  // a fallback for very old order shapes.

  const handleContinueShopping = () => {
    dispatch(resetCart());
    dispatch(resetOrderCreation());
    dispatch(resetShippingCost());
    setTrackOrder({});
    // localStorage.removeItem("eventData"); // remove the data from local storage
    dispatch(clearSSEData()); // Reset sseCard data

    history.push("/");
  };

  // If sseData?.data?.status === "successful, reset the cart and order creation
  useEffect(() => {
    if (sseData?.data?.status === "successful") {
      dispatch(resetCart());
      dispatch(resetOrderCreation());
    }
  }, [sseData?.data?.status, dispatch]);



  // Effect that clears SSE data when orderNumber is present
  useEffect(() => {
    if (orderNumber) {
      dispatch(resetCart());
    }
  }, [dispatch, orderNumber ]);

  // ── Terminal-state watchdog ───────────────────────────────────────────────
  //
  // This page waited on a socket alone and rendered OrderConfirmation
  // unconditionally — so a customer whose card was DECLINED, or whose 3-D
  // Secure page died, landed on an order confirmation with no payment status
  // shown anywhere. There was no failure state to render, and no poll to
  // discover one.
  //
  // `/fw/payment-status` can now report a real failure with the issuer's own
  // reason, and actively re-verifies with Flutterwave once an authorisation has
  // been quiet — so this is an oracle, not a hopeful retry. 5 minutes, because
  // bank OTP SMS is genuinely slow and some issuers add a second challenge.
  //
  // Timing out means "we stopped watching", never "it failed": the server keeps
  // verifying and flwReconciliationSweeper runs behind it.
  const POLL_MS = 4000;
  const DEADLINE_MS = 300000;
  const [payState, setPayState] = useState({ slow: false, timedOut: false, failed: null, authExpired: false });
  const settledRef = useRef(false);

  const liveOrder = trackOrder?._id ? trackOrder : sseData?.orderDetails;
  const watchOrderId = liveOrder?._id;
  const alreadyPaid =
    liveOrder?.isPaid === true || sseData?.data?.status === "successful";

  useEffect(() => {
    if (!watchOrderId || alreadyPaid) return undefined;
    let cancelled = false;
    const startedAt = Date.now();

    const timer = setInterval(async () => {
      if (cancelled || settledRef.current) return;
      if (Date.now() - startedAt >= DEADLINE_MS) {
        clearInterval(timer);
        setPayState((p) => ({ ...p, timedOut: true }));
        return;
      }
      try {
        const { data } = await userRequest(`/fw/payment-status/${watchOrderId}`);
        if (cancelled || !data) return;
        if (data.slow) setPayState((p) => ({ ...p, slow: true }));
        if (data.status === "successful") {
          settledRef.current = true;
          clearInterval(timer);
          // PRESERVE orderDetails. `setSSEData` REPLACES sseData wholesale
          // (redux/cardSseRedux.js), and every other dispatch in this file
          // carefully carries it forward. Without this the order summary —
          // number, items, totals — blanked out at the exact moment payment
          // confirmed, because the snapshot fetch is skipped precisely when
          // orderDetails already exists.
          dispatch(
            setSSEData({
              ...(sseDataRef.current || {}),
              orderDetails: sseDataRef.current?.orderDetails ?? null,
              data: { ...(sseDataRef.current?.data || {}), status: "successful" },
            })
          );
        } else if (data.status === "failed") {
          settledRef.current = true;
          clearInterval(timer);
          setPayState((p) => ({
            ...p,
            failed: data.reason || "Your card payment was declined.",
          }));
        }
      } catch (e) {
        // A 401 is NOT a payment problem, and must not be swallowed. The mobile
        // client silently refreshes on 401 (api/client.js monitor); the web has
        // no response interceptor, so without this the poll would keep ticking
        // with a dead token until the deadline and then report "we haven't
        // heard back" — turning an expired session into what looks like a
        // failed payment. Stop, and say the true thing.
        if (e?.response?.status === 401) {
          clearInterval(timer);
          setPayState((p) => ({ ...p, authExpired: true }));
          return;
        }
        // Anything else: a single blip must not end the wait — the deadline
        // above guarantees this loop terminates.
      }
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [watchOrderId, alreadyPaid, dispatch]);

  const payFailed = !!payState.failed;
  const payTimedOut = (payState.timedOut || payState.authExpired) && !alreadyPaid && !payFailed;
  const payState_ = alreadyPaid
    ? "ok"
    : payFailed
      ? "failed"
      : payTimedOut
        ? "unresolved"
        : "loading";

  return (
    <>
      <Navbar />
      <OrderConfirmation
        /* Live socket doc when we have one, else the created order.
           trackOrder starts as {} and is replaced on `orderUpdate`, so
           gate on _id rather than truthiness. */
        order={liveOrder}
        paymentLabel={"Card"}
        onContinueShopping={handleContinueShopping}
        paymentSlot={
          <PayRow $state={payState_}>
            <PayDot aria-hidden="true" />
            <div>
              <PayTitle>
                {alreadyPaid
                  ? "Payment confirmed"
                  : payFailed
                    ? "This payment did not go through"
                    : payTimedOut
                      ? "We haven't heard back from your bank yet"
                      : "Confirming your payment"}
              </PayTitle>
              <PayNote>
                {alreadyPaid
                  ? "Your card payment was authorised."
                  : payFailed
                    ? /* The issuer's own words — "Do not honour", "Insufficient
                         funds", "Incorrect OTP" are actionable in a way that a
                         bare "declined" is not. */
                      `${payState.failed} You have not been charged. If money did leave your account, contact support with your order number.`
                    : payTimedOut
                      ? /* NOT a verdict — never invite a second payment. */
                        payState.authExpired
                        ? "Your session expired while we were confirming. Sign in and check My Orders — if your bank approved it the payment will still have completed, so don't pay again."
                        : "If your bank approved it, the payment will still complete. Check My Orders in a few minutes before paying again."
                      : payState.slow
                        ? "Still waiting on your bank. Keep this page open — we'll confirm as soon as they respond."
                        : "This usually takes a few seconds."}
              </PayNote>
            </div>
          </PayRow>
        }
      />
    </>
  );
};

const PayRow = styled.div`
  color: ${(p) =>
    p.$state === "loading"
      ? "#8a9996"
      : p.$state === "failed"
        ? "#c0483a"
        : p.$state === "unresolved"
          ? "#b8860b" /* amber — not settled, but NOT a failure either */
          : "#5d8f24"};
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const PayDot = styled.span`
  flex: none;
  width: 10px;
  height: 10px;
  margin-top: 6px;
  border-radius: 50%;
  background: currentColor;
`;

const PayTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #14211f;
`;

const PayNote = styled.div`
  font-size: 13px;
  color: #4a5a57;
  margin-top: 2px;
`;

/*
 * A card payment that belongs to its own page (an Order together share)
 * goes back there instead (utils/cardPayment attachCardReturnPath). Decided
 * before this screen mounts: it reads the shopper's latest order and clears
 * carts, neither of which is about a share.
 */
const ReturnAfterCardPayment = ({ path }) => {
  useEffect(() => {
    clearCardAuthContext();
  }, []);
  return <Redirect to={path} />;
};

const CardPaymentDoneRoute = () => {
  const [returnPath] = useState(() => {
    const path = cardReturnPath();
    return isSafeReturnPath(path) ? path : null;
  });
  if (returnPath) return <ReturnAfterCardPayment path={returnPath} />;
  return <CardPaymentDoneScreen />;
};

export default CardPaymentDoneRoute;
